import { EmblaCarouselType } from 'embla-carousel';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EventListItem } from '../types/events.types';

import { DEFAULT_EVENT_IMAGE, FEATURED_SLIDER_FADE_DURATION, FEATURED_SLIDER_TRANSITION_DISTANCE } from '../constants';

type ManualTransition = {
  activeIndex: number;
  previousIndex: number;
  isForward: boolean;
};

type FeaturedSliderImagesProps = {
  events: EventListItem[];
  emblaApi: EmblaCarouselType | undefined;
  manualTransition?: ManualTransition | null;
};

const easeInOut = (t: number): number => {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
};

export const FeaturedSliderImages = ({ events, emblaApi, manualTransition }: FeaturedSliderImagesProps) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isForward, setIsForward] = useState(true);
  const [manualProgress, setManualProgress] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);

  // Handle manual transition (backward fade on main slider)
  useEffect(() => {
    if (manualTransition) {
      // Cancel any existing animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      setActiveIndex(manualTransition.activeIndex);
      setPreviousIndex(manualTransition.previousIndex);
      setIsForward(manualTransition.isForward);
      setIsAnimating(true);
      setManualProgress(0);
      startTimeRef.current = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTimeRef.current;
        const linearProgress = Math.min(elapsed / FEATURED_SLIDER_FADE_DURATION, 1);
        const easedProgress = easeInOut(linearProgress);
        setManualProgress(easedProgress);

        if (linearProgress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
          setManualProgress(1); // Ensure we end at exactly 1
          animationFrameRef.current = null;
        }
      };

      animationFrameRef.current = requestAnimationFrame(animate);
    }
    // Don't cleanup on manualTransition change - let animation complete
  }, [manualTransition]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const onSelect = useCallback(() => {
    if (!emblaApi || manualTransition) return;

    const newIndex = emblaApi.selectedScrollSnap();
    const oldIndex = activeIndex;

    if (newIndex !== oldIndex) {
      setPreviousIndex(oldIndex);
      setActiveIndex(newIndex);
      setIsForward(newIndex > oldIndex);
      setIsAnimating(true);
    }
  }, [emblaApi, activeIndex, manualTransition]);

  const onScroll = useCallback(() => {
    if (!emblaApi || manualTransition) return;

    const progress = emblaApi.scrollProgress();
    setScrollProgress(progress);
  }, [emblaApi, manualTransition]);

  const onSettle = useCallback(() => {
    if (manualTransition) return;
    setIsAnimating(false);
  }, [manualTransition]);

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.on('select', onSelect);
    emblaApi.on('scroll', onScroll);
    emblaApi.on('settle', onSettle);

    // Initialize
    if (!manualTransition) {
      setActiveIndex(emblaApi.selectedScrollSnap());
    }

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('scroll', onScroll);
      emblaApi.off('settle', onSettle);
    };
  }, [emblaApi, onSelect, onScroll, onSettle, manualTransition]);

  const getWrappedIndex = (baseIndex: number, offset: number): number => {
    const total = events.length;
    if (total === 0) return 0;
    return (baseIndex + offset + total) % total;
  };

  const getImagePosition = (index: number): 'main' | 'second' | 'third' | 'hidden' => {
    if (index === activeIndex) return 'main';
    if (index === getWrappedIndex(activeIndex, 1)) return 'second';
    if (index === getWrappedIndex(activeIndex, 2)) return 'third';
    return 'hidden';
  };

  const getImageStyles = (index: number, position: 'main' | 'second' | 'third' | 'hidden') => {
    const baseStyles = 'tw-rounded-[10px] tw-object-cover tw-shadow-[7px_0_7px_0_rgba(0,0,0,0.40)]';
    const sizeTransition = 'tw-transition-[width,height] tw-duration-300 tw-ease-in-out';

    const isTransitioningImage =
      (isForward && index === previousIndex && isAnimating) || (!isForward && index === activeIndex && isAnimating);

    if (isTransitioningImage) {
      return `${baseStyles} tw-absolute tw-z-[200] tw-w-full lg:tw-h-[360px] lg:tw-w-[640px]`;
    }

    switch (position) {
      case 'main':
        return `${baseStyles} ${sizeTransition} tw-relative tw-z-[100] tw-w-full lg:tw-h-[360px] lg:tw-w-[640px]`;
      case 'second':
        return `${baseStyles} ${sizeTransition} tw-absolute tw-hidden lg:tw-block tw-right-[-45px] tw-z-[3] tw-h-[338px] tw-w-[600px]`;
      case 'third':
        return `${baseStyles} ${sizeTransition} tw-absolute tw-hidden lg:tw-block tw-right-[-80px] tw-z-[2] tw-h-[315px] tw-w-[560px]`;
      default:
        return `${baseStyles} tw-hidden`;
    }
  };

  const getImageLeftPosition = (index: number): number | undefined => {
    if (!isAnimating) return undefined;

    // Use manual progress for backward fade, scrollProgress for normal Embla scroll
    let transitionProgress: number;

    if (manualTransition) {
      transitionProgress = manualProgress;
    } else {
      if (!emblaApi) return undefined;

      const slidesCount = events.length;
      if (slidesCount <= 1) return undefined;

      const targetProgress = activeIndex / (slidesCount - 1);
      const sourceProgress = previousIndex / (slidesCount - 1);
      const progressDiff = targetProgress - sourceProgress;

      if (progressDiff === 0) return undefined;

      transitionProgress = Math.abs((scrollProgress - sourceProgress) / progressDiff);
      transitionProgress = Math.max(0, Math.min(1, transitionProgress));
    }

    if (isForward && index === previousIndex) {
      return transitionProgress * -FEATURED_SLIDER_TRANSITION_DISTANCE;
    }

    if (!isForward && index === activeIndex) {
      return -FEATURED_SLIDER_TRANSITION_DISTANCE + transitionProgress * FEATURED_SLIDER_TRANSITION_DISTANCE;
    }

    return undefined;
  };

  return (
    <div className="tw-relative tw-flex tw-items-center lg:tw-h-[360px] lg:tw-w-[640px]">
      {events.map((event, index) => {
        const position = getImagePosition(index);
        const isTransitioningImage =
          (isForward && index === previousIndex && isAnimating) || (!isForward && index === activeIndex && isAnimating);

        if (position === 'hidden' && !isTransitioningImage) return null;

        const imageUrl = event.image_url || DEFAULT_EVENT_IMAGE;
        const leftPosition = getImageLeftPosition(index);
        const style = leftPosition !== undefined ? { left: `${leftPosition}px` } : undefined;

        return (
          <img
            key={event.id}
            src={imageUrl}
            alt={event.name}
            className={getImageStyles(index, position)}
            style={style}
          />
        );
      })}
    </div>
  );
};
